import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OverviewCard from './OverviewCard';
import defaultInstance from '../../axiosHelper';
import { API_ENDPOINTS } from '../../utils/apiEndPoints';
import { healthFacilitiesUpdate1, midoriTeamList } from '../../utils/helper';
import CustomHeader from '../header';
import AgeGroupItem from './AgeGroupItem';
import { getSessionFilter } from '../../utils/sessionStorage';

const CARD_CONFIG = [
    {
        title: 'Total Screenings',
        fields: [{ label: 'Total Screenings: ', key: 'total_screenings_till_date' }],
        background: 'rgba(255, 215, 0, 0.2)', // gold
        route: `/state-screening`
    },
    {
        title: 'CMHA Screen Health Facility',
        fields: [{ label: 'Health Facility: ', key: 'health_facility_count' }],
        background: 'rgba(30, 144, 255, 0.2)', // dodger blue
        route: '/health-facility-status',
    },
    {
        title: 'CMHA Screen Villages',
        fields: [{ label: 'Total Villages: ', key: 'village_count' }],
        background: 'rgba(255, 223, 0, 0.2)', // yellow
        route: '/villages-list',
    },
    {
        title: 'Screening Team',
        fields: [
            { label: 'Screening Count: ', key: 'team_screening_count' },
        ],
        background: 'rgba(106, 90, 205, 0.2)', // slate blue
        route: '/midori-teams'
    },
    {
        title: 'Sex',
        fields: [
            { label: 'Male: ', key: 'male_count' },
            { label: 'Female: ', key: 'female_count' },
        ],
        background: 'rgba(192, 192, 192, 0.2)', // silver
        route: '',
        showPercentage: true
    },
    {
        title: 'Risk Assessments',
        fields: [
            { label: 'At risk: ', key: 'at_risk_count' },
            { label: 'Not at risk: ', key: 'not_at_risk_count' },
        ],
        background: 'rgba(0, 128, 128, 0.2)', // teal
        route: '',
        showPercentage: true
    },
    {
        title: 'HTN Cases',
        fields: [
            { label: 'On treatment: ', key: 'htn_on_treatment_count' },
            { label: 'Not on treatment: ', key: 'htn_not_on_treatment_count' },
        ],
        background: 'rgba(144, 238, 144, 0.2)', // light green
        showPercentage: true,
        route: '/disease-screening',
        api_url: API_ENDPOINTS.GET_HTN_CASE_COUNT,
        page_title: 'HTN Cases by District'
    },
    {
        title: 'DM Cases',
        fields: [
            { label: 'On treatment: ', key: 'dm_on_treatment_count' },
            { label: 'Not on treatment: ', key: 'dm_not_on_treatment_count' },
        ],
        background: 'rgba(218, 112, 214, 0.2)', // orchid
        route: '/disease-screening',
        showPercentage: true,
        api_url: API_ENDPOINTS.GET_DM_CASE_COUNT,
        page_title: 'DM Cases by District'
    },
    {
        title: 'DM/HTN Cases',
        fields: [{ label: 'Total: ', key: 'dm_htn_count' }],
        background: 'rgba(255, 165, 0, 0.2)', // orange
        route: '',
        showPercentage: true
    },

    {
        title: 'Mental Health',
        fields: [
            { label: 'Total Count: ', key: 'mental_health' },
        ],
        background: 'rgba(186, 85, 211, 0.2)', // medium orchid
        route: '/disease-screening',
        api_url: API_ENDPOINTS.GET_MH_CASE_COUNT,
        page_title: 'Mental Health Cases by District',
        showPercentage: true
    },
    {
        title: 'Oral Cancer',
        fields: [
            { label: 'Total Count: ', key: 'oral_cancer' },
        ],
        background: 'rgba(255, 99, 132, 0.2)', // pink/red
        route: '/disease-screening',
        api_url: API_ENDPOINTS.GET_ORAL_CASE_COUNT,
        page_title: 'Oral Cancer Cases by District',
        showPercentage: true
    },
    {
        title: 'Breast Cancer',
        fields: [
            { label: 'Total Count: ', key: 'breast_cancer' },
        ],
        background: 'rgba(255, 140, 0, 0.2)', // dark orange
        route: '/disease-screening',
        api_url: API_ENDPOINTS.GET_BREAST_CANCER_CASE_COUNT,
        page_title: 'Breast Cancer Cases by District',
        showPercentage: true
    },
    {
        title: 'Cervical Cancer',
        fields: [
            { label: 'Total Count: ', key: 'cervical_cancer' },
        ],
        background: 'rgba(255, 215, 100, 0.2)', // light gold
        route: '/disease-screening',
        api_url: API_ENDPOINTS.GET_CERVICAL_CANCER_CASE_COUNT,
        page_title: 'Cervical Cancer Cases by District',
        showPercentage: true
    },
    {
        title: 'CVD',
        fields: [
            { label: 'Total Count: ', key: 'cvd' },
        ],
        background: 'rgba(175, 238, 238, 0.2)', // pale turquoise
        route: '/disease-screening',
        api_url: API_ENDPOINTS.GET_CVD_CASE_COUNT,
        page_title: 'CVD Cases by District',
        showPercentage: true
    },
    {
        title: 'COPD',
        fields: [
            { label: 'Total Count: ', key: 'copd' },
        ],
        background: 'rgba(30, 144, 255, 0.2)', // dodger blue
        route: '/disease-screening',
        api_url: API_ENDPOINTS.GET_COPD_CASE_COUNT,
        page_title: 'COPD Cases by District',
        showPercentage: true
    },
    {
        title: 'CKD',
        fields: [
            { label: 'Total Count: ', key: 'ckd' },
        ],
        background: 'rgba(138, 43, 226, 0.2)', // blue violet
        route: '/disease-screening',
        api_url: API_ENDPOINTS.GET_CKD_CASE_COUNT,
        page_title: 'CKD Cases by District',
        showPercentage: true
    },
    {
        title: 'Stroke History',
        fields: [
            { label: 'Total Count: ', key: 'post_stroke' },
        ],
        background: 'rgba(211, 211, 211, 0.2)', // light gray
        route: '/disease-screening',
        api_url: API_ENDPOINTS.GET_PS_CASE_COUNT,
        page_title: 'Post Stroke Cases by District',
        showPercentage: true
    },
    {
        title: 'BMI>=25',
        fields: [
            { label: 'Total Count: ', key: 'bmi' },
        ],
        background: 'rgba(60, 179, 113, 0.2)', // medium sea green
        route: '/disease-screening',
        api_url: API_ENDPOINTS.GET_BMI_CASE_COUNT,
        page_title: 'BMI>25 Cases by District',
        showPercentage: true
    },
    // {
    //     title: 'Age Groups',
    //     fields: [],
    //     background: 'rgba(100, 149, 237, 0.2)', // cornflower blue
    //     route: ''
    // },
];

const ageGrpConfig = {
    title: 'Age Groups',
    fields: [],
    background: 'rgba(100, 149, 237, 0.2)',
    route: ''
};

const OverView = () => {
    const navigate = useNavigate();
    const [ageGroups, setAgeGroups] = useState([]);
    const [counts, setCounts] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    const fetchCounts = async () => {
        try {
            setIsLoading(true)
            const { data } = await defaultInstance.get(API_ENDPOINTS.COUNT_TEST_SUMMARY);
            setIsLoading(false)
            if (data?.success) {
                setCounts({
                    dm_not_on_treatment_count: data?.dm_not_on_treatment_count || 0,
                    dm_on_treatment_count: data?.dm_on_treatment_count || 0,
                    htn_not_on_treatment_count: data?.htn_not_on_treatment_count || 0,
                    htn_on_treatment_count: data?.htn_on_treatment_count || 0,
                    dm_htn_count: data?.dm_htn_count || 0,
                    health_facility_count: healthFacilitiesUpdate1?.length || 238,
                    not_at_risk_count: data?.not_at_risk_count || 0,
                    male_count: data?.male_count || 0,
                    female_count: data?.female_count || 0,
                    at_risk_count: data?.at_risk_count || 0,
                    total_screenings_till_date: data?.total_screenings_till_date || 0,
                    village_count: 1396,
                    team_screening_count: midoriTeamList?.length || 0,
                    mental_health: data?.mentalhealthCount || 1840,
                    oral_cancer: data?.oralCancerCount || 41,
                    breast_cancer: data?.breastCancerCount || 103,
                    cervical_cancer: data?.cc_casesCount || 73,
                    cvd: data?.cvdCount || 934,
                    copd: data?.copdCount || 712,
                    ckd: data?.ckdCount || 3181,
                    post_stroke: data?.psCount || 1803,
                    bmi: data?.bmiCount || 141033,
                    district: data?.district || 10993
                });
                setAgeGroups(data?.ages || []);
            }
        } catch (error) {
            setIsLoading(false)
            console.error('Error fetching counts:', error);
        }
    };

    useEffect(() => {
        fetchCounts();
    }, []);

    if (isLoading) {
        return (<div style={{ width: '98vw', height: '95vh', position: 'absolute', alignItems: 'center', justifyContent: 'center', display: 'flex' }}>
            <img style={{ width: 50, height: 50 }} src='./loading.gif' alt='loading' />
        </div>)
    }

    return (
        <div className="role-container">
            <CustomHeader
                onBack={() => {
                    // Get any stored filter data if needed in the future
                    getSessionFilter('userFilter');
                    navigate('/users');
                }}
                // extraComp={<button className="custom-back-button" type='button' onClick={() => navigate('/district-screening')}>
                //     District Screening
                // </button>}
                hideHome
                headingComp={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                            <div style={{ fontSize: 25, color: '#926429', fontWeight: 'bold', textDecorationLine: 'underline' }}>CHIEF MINISTER’S HEALTH FOR ALL, MANIPUR</div>
                            <div className="card-content" style={{ marginTop: 3, fontSize: 20, color: '#926429', fontWeight: 'bold', textDecorationLine: 'underline' }}>DASHBOARD OVERVIEW</div>
                        </div>
                    </div>

                }
                secondaryHeading='MIDORI INFRATECH PVT. LTD'
                extraMenuItem='Home'
                onExtraItemClick={() => navigate(-1)}
            />
            {/* <div className='dashboard-overview'>
                <p>CHIEF MINISTER’S HEALTH FOR ALL, MANIPUR</p>
                <span>Technical partner</span>
                <p>MIDORI INFRATECH PVT. LTD</p>
                <span> DASHBOARD  OVER VIEW
                </span>
            </div> */}
            <div style={{ marginTop: 60 }} className="card-container">
                {CARD_CONFIG.map(({ title, fields, background, route, showPercentage, api_url, page_title }, index) => (
                    <OverviewCard
                        key={index}
                        style={{ backgroundColor: background }}
                        title={title}
                        data={fields}
                        values={counts}
                        type={''}
                        onClick={() => {
                            if (route) {
                                navigate(route, { state: { api_url, page_title } });
                            }
                        }}
                        showPercentage={showPercentage}
                        totalCount={counts?.total_screenings_till_date}
                    />
                ))}
                <AgeGroupItem
                    ageGroups={ageGroups}
                    ageGroupConfig={ageGrpConfig}
                    totalCount={counts?.total_screenings_till_date}
                />
            </div>
        </div>
    );
};

export default OverView;
